import { OFFERS_VISIBILITY_VALUES } from '~/constants/common'
import { modalUnsubscribeId } from '~/constants/modalNames'
import type { SellerProfile } from '~/types/seller'
import type { ModalSellerReviewParams } from '~/types/review'
import type { OrderRecord } from '~/types/checkout'
import type { ServerListResponse } from '~/types/api/response.types'

const defaultData: SellerProfile = {
  description: '',
  health: 0,
  hidden_offers_message: null,
  is_backoffice_available: false,
  is_online: false,
  mobile_phone_public: '',
  name: '',
  offers_count: 0,
  offers_visibility: OFFERS_VISIBILITY_VALUES.ALL,
  rating: 0,
  rating_delivery: 0,
  rating_product_description_fits: 0,
  rating_service: 0,
  reviews_count: 0,
  reviews_count_positive: 0,
  reviews_count_positive_percent: 0,
  show_reviews_count_positive_percent: false,
  slug: ',',
  small_logo: '',
  subscribers_count: 0,
  uuid: '',
  _is_subscribed: false
}

export const useSellerStore = defineStore('seller', {
  state: () => ({
    subscribeFetching: false,
    dataFetching: false,
    data: { ...defaultData } as SellerProfile
  }),
  getters: {
    hasRating: (state) => {
      return (
        state.data.rating_product_description_fits > 0 ||
        state.data.rating_delivery > 0 ||
        state.data.rating_delivery > 0
      )
    }
  },
  actions: {
    getLastOrderFromSeller({ sellerSlug, sellerName }: { sellerSlug: string, sellerName: string }) {
      const { $api } = useNuxtApp()

      return new Promise<ModalSellerReviewParams | null>((resolve, reject) => {
        $api<ServerListResponse<OrderRecord>>(`/api/users/profile/orders/?limit=1&offset=0&seller=${sellerSlug}&review=true`)
          .then(({ results, count }) => {
            if (count > 0) {
              resolve({
                sellerSlug,
                sellerName,
                lastOrder: results[0]
              })
            } else {
              resolve(null)
            }
          })
          .catch((e) => {
            reject(e)
          })
      })
    },

    async getSeller(sellerSlug: string) {
      const { $api } = useNuxtApp()

      this.dataFetching = true
      this.data = { ...defaultData }

      try {
        const seller = await $api<SellerProfile>(`/api/sellers/${encodeURIComponent(sellerSlug)}/`)

        this.data = seller
        return seller
      } finally {
        this.dataFetching = false
      }
    },

    async subscribe(sellerSlug: string) {
      const { $toast, $api, $i18n } = useNuxtApp()

      this.subscribeFetching = true

      try {
        const { success } = await $api<{ success: boolean }>(`/api/sellers/${sellerSlug}/subscribe/`, {
          method: 'POST'
        })

        if (success) {
          this.data._is_subscribed = true
          this.data.subscribers_count++

          $toast.success($i18n.t('seller.subscribe_success'))
        }
        return success
      } catch (e) {
        $toast.error($i18n.t('seller.subscribe_failure'))

        throw e
      } finally {
        this.subscribeFetching = false
      }
    },

    async unsubscribe(sellerSlug: string) {
      const { $vfm, $toast, $api, $i18n } = useNuxtApp()

      try {
        const { success } = await $api<{ success: boolean }>(`/api/sellers/${sellerSlug}/unsubscribe/`, {
          method: 'POST'
        })

        if (success) {
          this.data.subscribers_count--
          this.data._is_subscribed = false
          $toast.success($i18n.t('seller.unsubscribe_success'))
        }

        return success
      } catch (e) {
        $toast.error($i18n.t('seller.unsubscribe_failure'))

        throw e
      } finally {
        $vfm.close(modalUnsubscribeId)
      }
    }
  }
})
